import { TmsCollector, TmsCollectorConfig, WindowObject } from '@spartacus/tracking/tms/core'
import { Injectable } from '@angular/core'
import { CxEvent, LoginEvent } from '@spartacus/core'
import { SearchPageResultsEvent } from '@spartacus/storefront'
import { DownloadEvent } from '../../cms/components/shared/gtm-event/download-event.events'
import { VideoPlayEvent } from '../../cms/services/moving-image/video-events'
import { PageViewEvent } from '../../cms/page/page-view-event'
import { CartAddEntrySuccessEvent, CartPageEvent } from '@spartacus/cart/base/root'
import { OrderPlacedEvent } from '@spartacus/order/root'

// multiple mfq events can be pushed for a single event
type MbMfqMapperFn = ((e: CxEvent) => any[][] | undefined)[]

@Injectable({
  providedIn: 'root'
})
export class MbMfqCollectorService implements TmsCollector {

  mappers: MbMfqMapperFn = [
    this.cartPageView,
    this.cartAddAction,
    this.orderPageView,
    this.searchPageView,
    this.downloadAction,
    this.loginAction,
    this.videoPlayAction,
    this.pageView,
  ]

  init (config: TmsCollectorConfig,
       windowObject: WindowObject) {
  }

  pushEvent<T extends CxEvent> (config: TmsCollectorConfig,
                               windowObject: WindowObject,
                               event: T[]) {
    const mfqEvents = this.hasMapper(event)
    if (mfqEvents && mfqEvents.length) {
      const key = config.dataLayerProperty || '_mfq'
      const obj = windowObject[key] || []
      mfqEvents.forEach(e => e && obj.push(e))
    }
  }

  protected hasMapper<T extends CxEvent> (event: T) {
    for (const mapper of this.mappers) {
      const m = mapper.bind(this)(event)
      if (m && m.length) {
        return m
      }
    }
    return undefined
  }

  protected cartPageView<T extends CxEvent> (event: T) {
    return (event instanceof CartPageEvent) && [
      ['tag', 'cart']
    ] || undefined
  }

  protected cartAddAction<T extends CxEvent> (event: T) {
    return event instanceof CartAddEntrySuccessEvent && [
      ['tag', event.entry?.purchasable && 'add to cart' || 'request price']
    ] || undefined
  }

  protected orderPageView<T extends CxEvent> (event: T) {
    return event instanceof OrderPlacedEvent && [
      ['tag', 'purchase']
    ] || undefined
  }

  protected searchPageView<T extends CxEvent> (event: T) {
    return event instanceof SearchPageResultsEvent && [
      ['tag', 'search'],
      ['setVariable', 'Search-Term', event.searchTerm]
    ] || undefined
  }

  protected downloadAction<T extends CxEvent> (event: T) {
    return event instanceof DownloadEvent && [
      ['tag', 'download']
    ] || undefined
  }

  protected loginAction<T extends CxEvent> (event: T) {
    return event instanceof LoginEvent && [
      ['tag', 'logged in']
    ] || undefined
  }

  protected videoPlayAction<T extends CxEvent> (event: T) {
    return event instanceof VideoPlayEvent && [
      ['tag', 'video']
    ] || undefined
  }

  protected pageView<T extends CxEvent> (event: T): string[][] | undefined {
    if (event instanceof PageViewEvent) {
      const { pageType, businessSeg1, businessSeg2, businessSeg3 } = event.site
      return [
        ['tag', pageType || ''],
        ...businessSeg1 && [['tag', businessSeg1]] || [],
        ...businessSeg2 && [['tag', businessSeg2]] || [],
        ...businessSeg3 && [['tag', businessSeg3]] || [],
      ]
    }
    return undefined
  }
}
